"use client";

import { ArrowLeft, ArrowRight, Download, Minus, Plus, RotateCcw } from "lucide-react";
import { TextLayer, PDFDocumentProxy } from "pdfjs-dist";
import {
  useEffect,
  useRef,
  useState,
  useMemo,
  createRef,
  useCallback,
  useDeferredValue,
  Fragment,
  type Ref,
  type RefObject,
} from "react";

import { drawMetadata, clearHighlightCanvases } from "@/src/components/pdf-viewer/canvas-utils";
import { calculateMostVisiblePage } from "@/src/components/pdf-viewer/document-utils";
import { usePdfViewerInteractionContext } from "@/src/components/pdf-viewer/interaction-provider";
import { CancelableTask, PDFDocumentData } from "@/src/components/pdf-viewer/types";
import { usePdf } from "@/src/components/pdf-viewer/use-pdf";
import { Button } from "@/src/components/shadcn/ui/button";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { logger } from "@/src/lib/logging/logger";
import { cn } from "@/src/lib/shadcn-utils";
import { throttle } from "@/src/lib/throttle";
import { ExtractionMetadata } from "@/src/models/section";

export function PDFLoadingSkeleton() {
  return (
    <div className="flex h-full w-full min-h-0 min-w-0 flex-col">
      <div className="flex h-11 min-w-0 flex-shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b p-2">
        <div className="flex min-w-0 shrink-0 items-center justify-start">
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-[44px] px-1">
            <Skeleton className="h-3 w-full" />
          </div>
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center">
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <Minus size={16} />
          </Button>
          <span className="min-w-[50px] text-center text-muted-foreground text-xs">130%</span>
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <Plus size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center justify-end">
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <RotateCcw size={16} />
          </Button>
          <Button size="icon-sm" variant="ghost" disabled className="text-muted-foreground">
            <Download size={16} />
          </Button>
        </div>
      </div>
      <div className="relative flex flex-1 flex-col items-center justify-start overflow-auto bg-muted/40 pt-4 pb-6">
        <Skeleton className="mx-auto h-full w-[80%] rounded-sm" />
      </div>
    </div>
  );
}

function PDFError({ error }: { error: Error }) {
  return (
    <div className="h-full flex items-center justify-center overflow-scrolls">
      <p className="m-2 text-sm break-words overflow-hidden">Error loading file: {error?.message ?? "Unknown error"}</p>
    </div>
  );
}

function usePdfData({ document }: { document: PDFDocumentProxy | undefined }): PDFDocumentData[] | undefined {
  const documentDataRef = useRef<PDFDocumentData[]>([]);

  return useMemo(() => {
    if (!document) {
      documentDataRef.current = [];

      return undefined;
    }

    const data = Array.from({ length: document.numPages }, (_, pageNumber) => {
      const existing = documentDataRef.current[pageNumber];

      if (existing) {
        return {
          ...existing,
          pageNumber,
          viewport: null,
        };
      }

      return {
        pageNumber,
        canvasRef: createRef<HTMLCanvasElement | null>(),
        highlightRef: createRef<HTMLCanvasElement | null>(),
        textLayerRef: createRef<HTMLDivElement | null>(),
        viewport: null,
      };
    });

    documentDataRef.current = data;

    return data;
  }, [document]);
}

function assignRef<T>(ref: Ref<T | null> | undefined, value: T | null) {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
  } else {
    (ref as RefObject<T | null>).current = value;
  }
}

export function PDFViewer({
  url,
  activePage: propActivePage,
  filename,
  disableTextSelection = false,
  scrollContainerRef,
  pageFrameClassNames,
}: {
  url: string;
  activePage?: number;
  filename?: string;
  disableTextSelection?: boolean;
  scrollContainerRef?: Ref<HTMLDivElement | null>;
  pageFrameClassNames?: ReadonlyArray<string | null | undefined>;
}) {
  const {
    metadata,
    activePage: contextActivePage,
    boundingBoxStyle,
    focusedBoundingBoxIndex,
    hideNonFocusedBoxes,
  } = usePdfViewerInteractionContext();
  const activePage = propActivePage !== undefined ? propActivePage : contextActivePage;
  const { data: pdfDocument, isLoading, error } = usePdf(url);
  const documentData = usePdfData({ document: pdfDocument });

  const [pageRendering, setPageRendering] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const deferredCurrentPage = useDeferredValue(currentPage);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const setScrollContainerNode = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      assignRef(scrollContainerRef, node);
    },
    [scrollContainerRef]
  );
  const asyncTasksRef = useRef<CancelableTask[]>([]);
  const hasMetadataFocusScrolledRef = useRef(false);

  // Handle auto-scrolling when activePage prop changes
  useEffect(() => {
    if (activePage !== null && documentData && hasLoaded) {
      setCurrentPage(activePage);

      focusPage(activePage);
    }
  }, [activePage, documentData, hasLoaded]);

  // Handle page change on scroll
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const containerEl = containerRef.current;

    function handleScroll() {
      const document = calculateMostVisiblePage(containerRef.current, documentData ?? []);

      if (document) setCurrentPage(document.pageNumber);
    }

    const throttledScroll = throttle(handleScroll, 50);

    containerEl.addEventListener("scroll", throttledScroll);

    return () => {
      containerEl.removeEventListener("scroll", throttledScroll);
    };
  }, [documentData]);

  // Highlight metadata
  useEffect(() => {
    requestAnimationFrame(() => {
      if (!documentData) {
        return;
      }

      if (pageRendering) {
        return;
      }

      if (!metadata) {
        clearHighlightCanvases(documentData);

        return;
      }

      drawMetadata({
        documentData,
        metadata,
        style: boundingBoxStyle,
        focusedIndex: focusedBoundingBoxIndex,
        hideNonFocusedBoxes,
      });

      focusMetadata({
        documentData,
        metadata,
        focusedIndex: focusedBoundingBoxIndex,
      });
    });
  }, [metadata, documentData, boundingBoxStyle, focusedBoundingBoxIndex, hideNonFocusedBoxes, pageRendering]);

  async function focusMetadata({
    documentData,
    metadata,
    focusedIndex,
  }: {
    documentData: PDFDocumentData[];
    metadata: ExtractionMetadata;
    focusedIndex?: number | null;
  }) {
    const index = typeof focusedIndex === "number" ? focusedIndex : 0;
    const scrollBehavior: ScrollBehavior = "instant";
    const boundingBox = metadata.bboxes?.[index];

    if (!boundingBox) {
      return;
    }

    const [x, y, width, height] = boundingBox.bbox.xywh;
    const pageData = documentData[boundingBox.page];

    if (!pageData) {
      return;
    }

    const canvas = pageData.canvasRef.current;
    const { viewport } = pageData;

    if (!containerRef.current || !canvas || !viewport) {
      return;
    }

    const { rotation, width: viewportWidth, height: viewportHeight } = viewport;

    let scaledX, scaledY;

    // We need to transform our x/y coordinates based on the
    // viewport rotation.
    switch (rotation) {
      case 90:
        scaledX = (1 - y - height) * viewportWidth;

        scaledY = x * viewportHeight;
        break;
      case 180:
        scaledX = (1 - x - width) * viewportWidth;

        scaledY = (1 - y - height) * viewportHeight;
        break;
      case 270:
        scaledX = y * viewportWidth;

        scaledY = (1 - x - width) * viewportHeight;
        break;
      default:
        scaledX = x * viewportWidth;

        scaledY = y * viewportHeight;
        break;
    }

    const offsetTop = canvas.parentElement?.offsetTop ?? 0;
    const buffer = 20;

    containerRef.current.scrollTo({
      top: offsetTop + scaledY - buffer,
      left: scaledX - buffer,
      behavior: scrollBehavior,
    });
    hasMetadataFocusScrolledRef.current = true;
  }

  const autoScale = useCallback(async () => {
    if (!pdfDocument || !containerRef.current) {
      return;
    }

    const containerWidth = Math.min(containerRef.current.getBoundingClientRect().width, 1000);
    // Check the first two pages to get the max width of the doc.
    const pages = (
      await Promise.all([pdfDocument.getPage(1), pdfDocument.numPages > 1 ? pdfDocument.getPage(2) : undefined])
    ).filter((page) => page !== undefined);
    let maxViewportWidth = 0;

    pages.forEach((page) => {
      const viewport = page.getViewport({ scale: 1 });
      const viewportWidth = viewport.width;

      maxViewportWidth = Math.max(maxViewportWidth, viewportWidth);
    });

    const bufferX = 20 * 2;
    const newScale = containerWidth / (maxViewportWidth + bufferX);

    setScale(parseFloat(newScale.toFixed(1)));
  }, [pdfDocument]);

  const autoScaleOnLoad = useCallback(async () => {
    await autoScale();

    setHasLoaded(true);
  }, [setHasLoaded, autoScale]);

  const cancelRunningTasks = useCallback(async () => {
    await Promise.all(asyncTasksRef.current);

    asyncTasksRef.current = [];
  }, []);

  const renderText = useCallback(
    async (data: PDFDocumentData) => {
      const textLayerRef = data.textLayerRef.current;

      if (!pdfDocument || !textLayerRef || !data.viewport) {
        return;
      }

      const page = await pdfDocument.getPage(data.pageNumber + 1);

      const textContent = await page.getTextContent();

      if (textContent.items.length < 1) {
        return;
      }

      // Clear any previous text
      textLayerRef.innerHTML = "";

      const textLayer = new TextLayer({
        container: textLayerRef,
        textContentSource: textContent,
        viewport: data.viewport,
      });

      asyncTasksRef.current.push(() => {
        textLayer.cancel();
      });

      try {
        await textLayer.render();
        // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (e) {
        // do nothing if task is canceled.
      }
    },
    [pdfDocument]
  );

  const renderPage = useCallback(
    async (data: PDFDocumentData) => {
      if (!pdfDocument) return;

      try {
        const page = await pdfDocument.getPage(data.pageNumber + 1);
        const viewport = page.getViewport({ scale, rotation: ((page.rotate ?? 0) + rotation) % 360 });

        data.viewport = viewport;

        const pdfCanvas = data.canvasRef.current;
        const highlightCanvas = data.highlightRef.current;
        const textLayerRef = data.textLayerRef.current;

        if (!pdfCanvas || !highlightCanvas || !textLayerRef) {
          return;
        }

        const pixelRatio = window.devicePixelRatio ?? 1;
        const canvasHeight = Math.floor(viewport.height * pixelRatio);
        const canvasWidth = Math.floor(viewport.width * pixelRatio);
        const offscreenCanvas = document.createElement("canvas");

        offscreenCanvas.height = canvasHeight;
        offscreenCanvas.width = canvasWidth;

        const offscreenCtx = offscreenCanvas.getContext("2d");

        if (!offscreenCtx) {
          return;
        }

        const renderContext = {
          canvasContext: offscreenCtx,
          viewport,
        };

        offscreenCtx.scale(pixelRatio, pixelRatio);

        const renderTask = page.render(renderContext);

        asyncTasksRef.current.push(() => {
          renderTask.cancel();
        });

        try {
          await renderTask.promise;
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (err) {
          // do nothing if task is canceled.
          return;
        }

        // Skip stale output when refs were replaced while rendering.
        if (
          data.canvasRef.current !== pdfCanvas ||
          data.highlightRef.current !== highlightCanvas ||
          data.textLayerRef.current !== textLayerRef
        ) {
          return;
        }

        pdfCanvas.height = canvasHeight;
        pdfCanvas.width = canvasWidth;
        pdfCanvas.style.height = `${viewport.height}px`;
        pdfCanvas.style.width = `${viewport.width}px`;

        highlightCanvas.height = canvasHeight;
        highlightCanvas.width = canvasWidth;
        highlightCanvas.style.height = `${viewport.height}px`;
        highlightCanvas.style.width = `${viewport.width}px`;

        textLayerRef.style.height = `${viewport.height}px`;
        textLayerRef.style.width = `${viewport.width}px`;

        const pdfCtx = pdfCanvas.getContext("2d");
        const highlightCtx = highlightCanvas.getContext("2d");

        if (!pdfCtx || !highlightCtx) {
          return;
        }

        pdfCtx.clearRect(0, 0, canvasWidth, canvasHeight);
        pdfCtx.drawImage(offscreenCanvas, 0, 0);
        highlightCtx.scale(pixelRatio, pixelRatio);

        if (!disableTextSelection) {
          await renderText(data);
        }
      } catch (err) {
        logger.error("Error rendering page:", { err });
      }
    },
    [pdfDocument, renderText, rotation, scale, disableTextSelection]
  );

  const renderPages = useCallback(async () => {
    if (!documentData) {
      return;
    }

    setPageRendering(true);

    await cancelRunningTasks();

    await Promise.all(documentData.map((data) => renderPage(data)));

    setPageRendering(false);
  }, [documentData, renderPage, cancelRunningTasks]);

  // Rerender pages when viewport changes.
  useEffect(() => {
    if (documentData && documentData.length > 0) {
      if (!hasLoaded) {
        autoScaleOnLoad();

        return;
      }

      requestAnimationFrame(() => {
        renderPages();
      });
    }
  }, [documentData, scale, rotation, hasLoaded, autoScaleOnLoad, renderPages]);

  function rotatePDF() {
    setRotation((rotation) => (rotation + 270) % 360);
  }

  function changePage(offset: number) {
    if (!documentData) {
      return;
    }

    const newPage = deferredCurrentPage + offset;

    if (newPage < 0 || newPage > documentData.length - 1) {
      return;
    }

    setCurrentPage(newPage);

    focusPage(newPage);
  }

  const focusPage = useCallback(
    (pageNumber: number) => {
      const canvas = documentData?.[pageNumber]?.canvasRef.current;

      if (canvas) {
        canvas.scrollIntoView({
          block: "start",
        });
      }
    },
    [documentData]
  );

  if (isLoading) {
    return <PDFLoadingSkeleton />;
  }

  if (!pdfDocument) {
    return <PDFError error={error as Error} />;
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-col w-full h-full">
      <div className="flex min-w-0 flex-shrink-0 flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b p-2">
        <div className="flex min-w-0 shrink-0 items-center justify-start">
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => changePage(-1)}
            disabled={deferredCurrentPage <= 0}
            className="text-muted-foreground"
          >
            <ArrowLeft size={16} />
          </Button>
          <span className="min-w-[44px] text-center text-muted-foreground text-xs">
            {deferredCurrentPage + 1} / {pdfDocument.numPages}
          </span>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => changePage(1)}
            disabled={deferredCurrentPage >= pdfDocument.numPages - 1}
            className="text-muted-foreground"
          >
            <ArrowRight size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center">
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => setScale((scale) => Math.max(0.1, scale - 0.1))}
            className="text-muted-foreground"
          >
            <Minus size={16} />
          </Button>
          <span className="min-w-[50px] text-center text-muted-foreground text-xs">{Math.round(scale * 100)}%</span>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => setScale((scale) => Math.min(3, scale + 0.1))}
            className="text-muted-foreground"
          >
            <Plus size={16} />
          </Button>
        </div>
        <div className="flex shrink-0 items-center justify-end">
          <Button size="icon-sm" variant="ghost" className="text-muted-foreground" onClick={rotatePDF}>
            <RotateCcw size={16} />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            className="text-muted-foreground"
            onClick={async () => {
              try {
                const pdfData = await pdfDocument.getData();

                // Check if File System Access API is supported using feature detection
                // @ts-ignore - File System Access API may not be recognized by TypeScript
                if (typeof window.showSaveFilePicker === "function") {
                  try {
                    // Use the File System Access API to save the file
                    // This allows users to select a download location
                    // @ts-ignore - Using the File System Access API
                    const fileHandle = await window.showSaveFilePicker({
                      suggestedName: filename || "document.pdf",
                      types: [
                        {
                          description: "PDF Files",
                          accept: { "application/pdf": [".pdf"] },
                        },
                      ],
                    });

                    // @ts-ignore - Using the File System Access API
                    const writable = await fileHandle.createWritable();

                    await writable.write(pdfData);

                    await writable.close();

                    return;
                  } catch (saveError: unknown) {
                    // User cancelled the save dialog - don't proceed to fallback
                    logger.info("Save picker was cancelled:", { error: saveError });

                    // Only proceed to fallback if error doesn't seem like a user cancellation
                    // The AbortError is what's thrown when a user cancels the dialog
                    if (saveError instanceof Error && saveError.name !== "AbortError") {
                      throw saveError; // Re-throw to trigger the fallback
                    }

                    return; // Exit early if user cancelled
                  }
                }

                // Fall back to regular download if API not supported
                const a = document.createElement("a");

                a.href = url;

                a.download = filename || "document.pdf";

                document.body.appendChild(a);

                a.click();

                document.body.removeChild(a);
              } catch (error) {
                logger.error("Error downloading file:", { error });
              }
            }}
          >
            <Download size={16} />
          </Button>
        </div>
      </div>
      <div
        className="relative flex flex-col justify-start items-center flex-1 overflow-auto p-2 bg-muted/40 gap-4"
        ref={setScrollContainerNode}
      >
        {documentData?.map((data) => {
          const userUnit = data.viewport?.userUnit ?? 1;
          const showPage = Boolean(data.canvasRef.current?.style.width);

          return (
            <Fragment key={`page-${data.pageNumber}`}>
              <div
                className={cn(
                  "relative isolate mx-auto",
                  !showPage && "invisible",
                  pageFrameClassNames?.[data.pageNumber]
                )}
              >
                <canvas ref={data.canvasRef} className="border border-gray-300 shadow-md" />
                <canvas ref={data.highlightRef} className="absolute inset-0 pointer-events-none z-10" />
                <div
                  ref={data.textLayerRef}
                  className="absolute inset-0 z-20 opacity-20 text-transparent leading-[1] overflow-hidden [&>span]:absolute [&>span]:-ml-[1ch]"
                  data-main-rotation={rotation}
                  style={{
                    // see https://github.com/mozilla/pdf.js/blob/d009e4b3a743661d91d4c0d97367b7abe51d2b18/web/pdf_viewer.css#L110
                    // @ts-expect-error css var ok
                    "--total-scale-factor": scale * userUnit,
                    "--user-unit": userUnit,
                    "--scale-round-y": "1px",
                    "--scale-round-x": "1px",
                  }}
                />
              </div>
              <hr
                className={`last:hidden h-[1px] w-full border border-dashed border-accent-foreground ${showPage ? "" : "invisible"}`}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
